import React from 'react';
import { motion } from 'framer-motion';
import { Users, FileText, Phone, TrendingUp, Eye, Edit, Calendar } from 'lucide-react';
import { getUsers, getPageData, getContactData } from '../utils/auth';

const Dashboard = () => {
  const users = getUsers();
  const contactData = getContactData();
  const activeUsers = users.filter(u => u.isActive).length;

  const stats = [
    { name: 'Total Users', value: users.length, icon: Users, color: 'bg-blue-500' },
    { name: 'Active Users', value: activeUsers, icon: TrendingUp, color: 'bg-green-500' },
    { name: 'Pages Managed', value: 6, icon: FileText, color: 'bg-purple-500' },
    { name: 'Contact Methods', value: Object.keys(contactData).length, icon: Phone, color: 'bg-orange-500' }
  ];

  const recentActivity = [
    {
      id: 1,
      action: 'Page Updated',
      details: 'Home page content modified',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      icon: Edit
    },
    {
      id: 2,
      action: 'User Created',
      details: 'New admin user added',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      icon: Users
    },
    {
      id: 3,
      action: 'Contact Updated',
      details: 'Phone number changed',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      icon: Phone
    }
  ];

  const quickActions = [
    {
      name: 'Edit Home Page',
      description: 'Update hero section and content',
      href: '/admin/pages?page=home',
      icon: FileText,
      color: 'bg-blue-600'
    },
    {
      name: 'Manage Users',
      description: 'Add or remove admin users',
      href: '/admin/users',
      icon: Users,
      color: 'bg-green-600'
    },
    {
      name: 'Update Contact',
      description: 'Modify contact information',
      href: '/admin/contact',
      icon: Phone,
      color: 'bg-purple-600'
    },
    {
      name: 'View Website',
      description: 'Check the live website',
      href: '/',
      icon: Eye,
      color: 'bg-orange-600'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Welcome back! Here's what's happening with your website.
          </p>
        </div>
        <div className="text-sm text-gray-500 flex items-center">
          <Calendar className="w-4 h-4 mr-2" />
          {new Date().toLocaleDateString()}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center">
              <div className={`${stat.color} rounded-lg p-3`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
          <div className="space-y-4">
            {quickActions.map((action) => (
              <a
                key={action.name}
                href={action.href}
                className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <div className={`${action.color} rounded-lg p-2`}>
                  <action.icon className="w-5 h-5 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="font-medium text-gray-900">{action.name}</h3>
                  <p className="text-sm text-gray-600">{action.description}</p>
                </div>
              </a>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent Activity</h2>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center p-4 bg-gray-50 rounded-lg">
                <div className="bg-blue-100 rounded-lg p-2">
                  <activity.icon className="w-5 h-5 text-blue-600" />
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="font-medium text-gray-900">{activity.action}</h3>
                  <p className="text-sm text-gray-600">{activity.details}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(activity.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* System Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-6">System Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-2"></div>
            <p className="text-sm font-medium text-gray-900">Website</p>
            <p className="text-xs text-gray-600">Online</p>
          </div>
          <div className="text-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-2"></div>
            <p className="text-sm font-medium text-gray-900">Admin Panel</p>
            <p className="text-xs text-gray-600">Active</p>
          </div>
          <div className="text-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-2"></div>
            <p className="text-sm font-medium text-gray-900">Data Storage</p>
            <p className="text-xs text-gray-600">Operational</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;
